/**
 * `useTremblementSM` — le tremblement de la zone de jeu en zone rouge de santé mentale.
 *
 * SPEC-UI §7.5 / PLAN §4.10 : *« dans l'Atelier, tremblement de la zone de jeu : `translate`
 * aléatoire ±1 px toutes les 280 ms (désactivé si reduced-motion) »*. Les cinq mini-jeux
 * l'appellent sur leur conteneur — un hook plutôt que cinq copies, comme `useChronoSonore`.
 *
 * ## Ce qu'il ne touche PAS, et c'est le contrat
 *
 * C'est un `style` **purement décoratif** : ±1 px sur un conteneur ne déplace aucune hitbox
 * relative (tout l'intérieur bouge d'un bloc, et les frappes des mini-jeux sont des instants,
 * pas des positions d'écran — sauf le Tournage, où le pointeur se mesure DANS la scène, qui
 * tremble avec lui). Aucun barème ne lit ce hook, aucune timeline ne l'embarque : le serveur
 * note exactement pareil, joueur secoué ou pas — c'est le même invariant que la musique
 * (⟦A-22⟧, règle de relecture en tête de `audio/beatmap.ts`).
 *
 * Le « hasard » est un hachage de l'index du tic, pas `Math.random()` : même sur du décor, un
 * rendu reproductible se relit — et personne ne vient chercher un tirage non seedé dans un
 * mini-jeu par ailleurs entièrement déterministe.
 */

import { useEffect, useState, type CSSProperties } from "react";

import type { GameState } from "@maxime/shared";

import { zoneSM } from "../../game/format";
import { useUIStore } from "../../stores/uiStore";

/** La période de la spec, en ms — « toutes les 280 ms ». */
export const TREMBLEMENT_PERIODE_MS = 280;

/** Hachage → [0 ; 1[. Le classique fract(sin(n)·43758,5453), suffisant pour du décor. */
function bruit(n: number): number {
  const v = Math.sin(n * 12.9898) * 43758.5453;
  return v - Math.floor(v);
}

/** ±1 px, jamais plus : un tremblement, pas un séisme. */
function pixel(n: number): number {
  return Math.round(bruit(n) * 2 - 1);
}

/**
 * Rend le `style` à poser sur la zone de jeu — `undefined` hors zone rouge ou sous
 * `prefers-reduced-motion` (§7.4 : toutes les animations décoratives se désactivent).
 */
export function useTremblementSM(state: GameState): CSSProperties | undefined {
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const actif = !reducedMotion && zoneSM(state.mental) === "rouge";
  const [tic, setTic] = useState(0);

  useEffect(() => {
    if (!actif) return;
    const minuterie = window.setInterval(
      () => setTic((n) => n + 1),
      TREMBLEMENT_PERIODE_MS,
    );
    return () => window.clearInterval(minuterie);
  }, [actif]);

  if (!actif) return undefined;
  return { transform: `translate(${pixel(tic * 2 + 1)}px, ${pixel(tic * 2 + 2)}px)` };
}
